import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";

type Subscription = {
  id: string;
  status: string;
  billingInterval: "monthly" | "annual";
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
};

const tan15 = "rgba(201,147,76,0.15)";
const tan40 = "rgba(201,147,76,0.40)";
const navy73 = "rgba(8,45,61,0.73)";
const navy47 = "rgba(8,45,61,0.47)";
const navy80 = "rgba(8,45,61,0.80)";

/* ── sidebar nav icons ── */
function Icon({ name }: { name: "feed" | "videos" | "downloads" | "announcements" | "account" }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 18 18",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "feed":
      return (
        <svg {...common}>
          <path d="M2 9L9 2l7 7" />
          <path d="M4 7v8h4v-5h2v5h4V7" />
        </svg>
      );
    case "videos":
      return (
        <svg {...common}>
          <circle cx="9" cy="9" r="7" />
          <polygon points="7.5,6.5 12.5,9 7.5,11.5" fill="currentColor" stroke="none" />
        </svg>
      );
    case "downloads":
      return (
        <svg {...common}>
          <path d="M9 2v9" />
          <path d="M5.5 7.5L9 11l3.5-3.5" />
          <path d="M3 14h12" />
        </svg>
      );
    case "announcements":
      return (
        <svg {...common}>
          <path d="M3 7h9l3-3v9l-3-3H3z" />
          <path d="M3 11v3" />
        </svg>
      );
    case "account":
      return (
        <svg {...common}>
          <circle cx="9" cy="6" r="3" />
          <path d="M2 16c0-3.314 3.134-6 7-6s7 2.686 7 6" />
        </svg>
      );
  }
}

function NavItem({
  to,
  icon,
  label,
  active,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  active?: boolean;
}) {
  const style: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    borderRadius: 999,
    fontFamily: fonts.body,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    color: active ? colors.berryRose : navy80,
    background: active ? colors.blushPink : "transparent",
    transition: "background 0.15s, color 0.15s",
  };
  return (
    <Link to={to} style={style} className="aflt-navitem">
      <span style={{ opacity: active ? 1 : 0.75, display: "inline-flex" }}>{icon}</span>
      {label}
    </Link>
  );
}

function LockOverlay() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(255,255,255,0.60)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        borderRadius: 16,
        boxShadow: `inset 0 0 0 1px ${tan15}`,
      }}
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        stroke={colors.navy}
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ opacity: 0.9 }}
      >
        <rect x="9" y="19" width="22" height="16" rx="4" />
        <path d="M14 19v-6a6 6 0 0112 0v6" />
      </svg>
      <span
        style={{
          fontFamily: fonts.body,
          fontSize: 13,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.18em",
          color: colors.navy,
        }}
      >
        Members only
      </span>
    </div>
  );
}

const cardStyle: CSSProperties = {
  background: "#ffffff",
  borderRadius: 16,
  border: `1px solid ${tan15}`,
  padding: 28,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const eyebrowStyle: CSSProperties = {
  fontFamily: fonts.body,
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.2em",
  color: colors.berryRose,
};

const cardTitleStyle: CSSProperties = {
  fontFamily: fonts.logo,
  fontSize: 19,
  fontWeight: 500,
  color: colors.navy,
  lineHeight: 1.35,
};

const cardBodyStyle: CSSProperties = {
  fontFamily: fonts.body,
  fontSize: 14,
  fontWeight: 400,
  color: navy73,
  lineHeight: 1.6,
};

function AnnouncementCard() {
  return (
    <div style={cardStyle}>
      <span style={eyebrowStyle}>Announcement</span>
      <h3 style={cardTitleStyle}>Spring Quilt Along Starts Monday</h3>
      <p style={cardBodyStyle}>
        We&rsquo;re kicking off the spring quilt-along this Monday and I can&rsquo;t wait to sew
        alongside all of you. Blocks are beginner-friendly but there&rsquo;s plenty of room to add
        your own flair.
      </p>
      <p style={cardBodyStyle}>
        The full supply list is already waiting for you in Downloads &mdash; grab it before the
        weekend so you have everything on hand when we start.
      </p>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: 8,
          borderTop: `1px solid ${tan15}`,
          marginTop: 4,
        }}
      >
        <span style={{ fontSize: 12, color: navy47 }}>Apr 24, 2026</span>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: navy73, fontWeight: 500 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 13.5S1.5 9.5 1.5 5.25a3.25 3.25 0 016.5-.5 3.25 3.25 0 016.5.5C14.5 9.5 8 13.5 8 13.5z" />
            </svg>
            28
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: navy73, fontWeight: 500 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h12v8H9l-3 2.5V11H2z" />
            </svg>
            12
          </span>
        </div>
      </div>
    </div>
  );
}

function VideoCardInner() {
  return (
    <div style={cardStyle}>
      <span style={eyebrowStyle}>New Video</span>
      <div
        style={{
          width: "100%",
          aspectRatio: "16 / 9",
          borderRadius: 10,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${colors.berryPink} 0%, ${colors.tan} 55%, ${colors.softLilac} 100%)`,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 2px, transparent 2px, transparent 10px)",
          }}
        />
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 16px rgba(8,45,61,0.18)",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <polygon points="7,5 17,10 7,15" fill={colors.berryRose} />
          </svg>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 10,
            right: 12,
            background: "rgba(8,45,61,0.75)",
            color: "#fff",
            fontFamily: fonts.body,
            fontSize: 12,
            fontWeight: 600,
            padding: "3px 9px",
            borderRadius: 999,
            letterSpacing: "0.04em",
          }}
        >
          18:42
        </div>
      </div>
      <h3 style={cardTitleStyle}>Binding a Quilt by Hand</h3>
      <p style={cardBodyStyle}>Join Chelsea for a slow, satisfying look at hand binding your quilt.</p>
      <div style={{ paddingTop: 8, borderTop: `1px solid ${tan15}`, marginTop: 4 }}>
        <span style={{ fontSize: 12, color: navy47 }}>Apr 21, 2026</span>
      </div>
    </div>
  );
}

function DownloadCardInner({ memberView }: { memberView: boolean }) {
  return (
    <div style={cardStyle}>
      <span style={eyebrowStyle}>New Download</span>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div
          style={{
            flexShrink: 0,
            width: 44,
            height: 52,
            borderRadius: 8,
            background: "#e24040",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: fonts.body,
            fontSize: 11,
            fontWeight: 700,
            color: "#fff",
            letterSpacing: "0.06em",
          }}
        >
          PDF
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <h3 style={{ ...cardTitleStyle, fontSize: 18 }}>Free Pattern: Scrappy Tote Bag</h3>
          <p style={{ fontFamily: fonts.body, fontSize: 12, color: navy47, letterSpacing: "0.04em" }}>
            PDF Pattern &middot; 2.4 MB
          </p>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: 8,
          borderTop: `1px solid ${tan15}`,
        }}
      >
        <span style={{ fontSize: 12, color: navy47 }}>Apr 18, 2026</span>
        {memberView && (
          <button type="button" style={pillButtonStyle("0.68rem", "9px 20px")}>
            Download
          </button>
        )}
      </div>
    </div>
  );
}

function pillButtonStyle(fontSize: string, padding: string): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    padding,
    borderRadius: 999,
    background: colors.berryRose,
    color: "#fff",
    fontFamily: fonts.body,
    fontSize,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    border: "none",
    cursor: "pointer",
    textDecoration: "none",
  };
}

function Locked({ children }: { children: ReactNode }) {
  return (
    <div style={{ position: "relative", borderRadius: 16, overflow: "hidden" }}>
      <div style={{ filter: "blur(6px)", userSelect: "none", pointerEvents: "none" }}>{children}</div>
      <LockOverlay />
    </div>
  );
}

export function Feed() {
  const { data: session, isPending } = useSession();
  const nav = useNavigate();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isPending) return;
    if (!session?.user) {
      nav("/login");
      return;
    }
    api
      .get<{ subscription: Subscription | null }>("/api/account/subscription")
      .then((r) => setSub(r.subscription))
      .catch(() => setSub(null))
      .finally(() => setLoaded(true));
  }, [session, isPending, nav]);

  const member = !!sub && (sub.status === "active" || sub.status === "trialing");
  const email = session?.user?.email ?? "";
  const initial = (email.charAt(0) || "S").toUpperCase();

  return (
    <div style={{ minHeight: "100vh", background: colors.cream }}>
      {/* ── sidebar ── */}
      <aside
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 240,
          height: "100vh",
          background: colors.cream,
          borderRight: `1px solid ${tan15}`,
          display: "flex",
          flexDirection: "column",
          padding: "28px 0 24px",
          zIndex: 100,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 20px 28px", borderBottom: `1px solid ${tan15}` }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${colors.berryPink} 0%, ${colors.tan} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: fonts.logo,
              fontSize: 22,
              color: "#fff",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            C
          </div>
          <img
            src="/logos/text-logo-purple.png"
            alt="A Few Loose Threads"
            style={{ height: 30, width: "auto" }}
            width={500}
            height={185}
          />
        </div>

        <nav style={{ flex: 1, padding: "20px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
          <NavItem to="/feed" icon={<Icon name="feed" />} label="Feed" active />
          <NavItem to="/feed" icon={<Icon name="videos" />} label="Videos" />
          <NavItem to="/feed" icon={<Icon name="downloads" />} label="Downloads" />
          <NavItem to="/feed" icon={<Icon name="announcements" />} label="Announcements" />
          <NavItem to="/account" icon={<Icon name="account" />} label="Account" />
        </nav>

        <div style={{ padding: "16px 20px 0", borderTop: `1px solid ${tan15}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${colors.softLilac}, ${colors.berryPink})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: fonts.body,
              fontSize: 13,
              fontWeight: 600,
              color: colors.navy,
              flexShrink: 0,
            }}
          >
            {initial}
          </div>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.navy,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 150,
              }}
            >
              {email || "Guest"}
            </span>
            <span style={{ fontSize: 11, color: navy47, letterSpacing: "0.04em" }}>
              {member ? "Member" : "Free"}
            </span>
          </div>
        </div>
      </aside>

      {/* ── main ── */}
      <main style={{ marginLeft: 240, minHeight: "100vh", display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 760, padding: "48px 40px 80px" }}>
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontFamily: fonts.logo, fontSize: 28, fontWeight: 400, color: colors.navy, lineHeight: 1.2 }}>
              Feed
            </h1>
          </div>

          {isPending || !loaded ? (
            <p style={{ ...cardBodyStyle }}>Loading…</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {!member && (
                <div
                  style={{
                    background: colors.blushPink,
                    borderRadius: 16,
                    border: "1px solid rgba(212,96,106,0.18)",
                    padding: "32px 36px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <h2 style={{ fontFamily: fonts.logo, fontSize: 26, fontWeight: 500, color: colors.navy, lineHeight: 1.2 }}>
                    Join the club
                  </h2>
                  <p style={{ fontFamily: fonts.body, fontSize: 15, color: navy73, lineHeight: 1.55, maxWidth: 440 }}>
                    Weekly videos, sewing patterns, downloads, and a cozy community.
                  </p>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                    <Link to="/account/subscribe" style={pillButtonStyle("0.72rem", "11px 24px")}>
                      Become a member
                    </Link>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {["$8/mo", "$80/yr"].map((p) => (
                        <span
                          key={p}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "7px 16px",
                            borderRadius: 999,
                            background: "#fff",
                            border: `1px solid ${tan40}`,
                            fontFamily: fonts.body,
                            fontSize: 13,
                            fontWeight: 600,
                            color: navy80,
                            letterSpacing: "0.02em",
                          }}
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* teaser announcement — always visible */}
              <AnnouncementCard />

              {/* video + download — locked for non-members */}
              {member ? (
                <>
                  <VideoCardInner />
                  <DownloadCardInner memberView />
                </>
              ) : (
                <>
                  <Locked>
                    <VideoCardInner />
                  </Locked>
                  <Locked>
                    <DownloadCardInner memberView={false} />
                  </Locked>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
